import { type ReactNode, useEffect } from 'react';
import { useSessionGuardState } from '../hooks/useSessionGuardState';
import { useRouter } from 'next/router';
import Spinner from '@erikdakoda/ui/components/SpinnerOverlay';
import authConfig from '@erikdakoda/auth/shared/config';
import { getAuthGuardAddons } from '../shared/authGuardAddons';
import TimezoneUpdater from './TimezoneUpdater';
import BlankLayout from '@erikdakoda/site-template-ui/layouts/BlankLayout';
import Error401Page from '../pages/Error401Page';
import { identifyClient } from '@erikdakoda/analytics/client/identify';
import { isPrivilegedAppPath } from '../shared/isPrivilegedAppPath';

type AuthGuardProps = {
  children: ReactNode | ReactNode[];
};

export default function AuthGuard({ children }: AuthGuardProps) {
  'use memo';

  const { session, isInitialSessionLoad } = useSessionGuardState();
  const router = useRouter();
  const isAdminPage = isPrivilegedAppPath(router.asPath);
  const userRole = session?.user?.role ?? 'USER';
  const pages = authConfig.config.pages;

  useEffect(() => {
    const user = session?.user;
    if (!user?.id) {
      return;
    }

    identifyClient(user.id, {
      onboarding_track: user.onboardingTrack,
      coach_id: user.coachId,
      gender: user.gender,
      country: user.country,
      state: user.state,
      acq_channel: user.acqChannel,
      utm_source: user.utmSource,
      utm_medium: user.utmMedium,
      utm_campaign: user.utmCampaign,
      ...(process.env.NEXT_PUBLIC_ANALYTICS_ALLOW_PII === 'true' && {
        email: user.email,
      }),
    });
  }, [session?.user]);

  if (
    [pages.signup, pages.login, pages.verifyToken, pages.reset, pages.security].includes(
      router.pathname,
    )
  ) {
    return children;
  }

  if (isInitialSessionLoad) {
    return (
      <BlankLayout className="auth-guard">
        <Spinner />
      </BlankLayout>
    );
  } else if (!session) {
    if (router.asPath === pages.home) {
      router.replace(pages.login);
    } else {
      router.replace({
        pathname: pages.login,
        query: { returnUrl: router.asPath },
      });
    }
    return null;
  } else if (
    !session.user?.emailVerified &&
    router.asPath !== pages.security &&
    router.asPath !== pages.verify
  ) {
    router.replace(pages.verify);
    return null;
  } else if (isAdminPage && !['ADMIN', 'TEAM'].includes(userRole)) {
    return (
      <BlankLayout className="auth-guard">
        <Error401Page />
      </BlankLayout>
    );
  } else {
    return (
      <>
        {children}
        {getAuthGuardAddons().map((Addon, index) => (
          <Addon key={index} className="auth-guard" />
        ))}
        <TimezoneUpdater className="auth-guard" />
      </>
    );
  }
}
